package gr.tech.GRAttestation.infrastructure.api

import gr.tech.GRAttestation.models.api.ApiResponse
import gr.tech.GRAttestation.models.api.PaginatedResult
import gr.tech.GRAttestation.models.storeusers.SendInviteEmailRequest
import gr.tech.GRAttestation.models.storeusers.STORE_USERS_STATUS
import gr.tech.GRAttestation.models.storeusers.StoreAddUsersRequest
import gr.tech.GRAttestation.models.storeusers.StoreUsersDTO
import gr.tech.GRAttestation.models.tables.BasicTableFilters
import gr.tech.GRAttestation.util.ROUTES
import gr.tech.models.storeusers.StoreUsersInviteDTO
import kotlinx.serialization.json.Json

suspend fun adminCreateNewStoreUser(
    newStoreUser: StoreAddUsersRequest,
    onSuccess: (ApiResponse<StoreUsersInviteDTO>) -> Unit,
    onError: (String) -> Unit,
) {
    try {
        val result: ApiResponse<StoreUsersInviteDTO> = safeApiCallStringBody(
            url = ROUTES.BACKEND.STORES.ADMIN_STORE_USERS_INVITES,
            method = "POST",
            body = Json.encodeToString(newStoreUser)
        )
        onSuccess(result)
    } catch (e: Exception) {
        onError("failed to adminCreateNewStoreUser(): $e")
        return
    }
}

suspend fun adminSendStoreUserInviteEmail(
    storeId: Int,
    inviteUuid: String,
    sendToEmail: String,
    onSuccess: (ApiResponse<Boolean>) -> Unit,
    onError: (String) -> Unit,
) {
    try {
        val result: ApiResponse<Boolean> = safeApiCallStringBody(
            url = "${ROUTES.BACKEND.STORES.ADMIN_STORE_USERS_INVITES}/$storeId/$inviteUuid/send-email",
            method = "POST",
            body = Json.encodeToString(SendInviteEmailRequest(sendToEmail = sendToEmail))
        )
        onSuccess(result)
    } catch (e: Exception) {
        onError("failed to adminSendStoreUserInviteEmail(): $e")
    }
}

suspend fun adminUpdateStoreUser(
    updatedStoreUsersDTO: StoreUsersDTO,
    onSuccess: (ApiResponse<StoreUsersDTO>) -> Unit,
    onError: (String) -> Unit,
) {
    try {
        val result: ApiResponse<StoreUsersDTO> = safeApiCallStringBody(
            url = "${ROUTES.BACKEND.STORES.ADMIN_STORE_USERS}/${updatedStoreUsersDTO.storeId}/${updatedStoreUsersDTO.uuid}",
            method = "PUT",
            body = Json.encodeToString(updatedStoreUsersDTO)
        )
        onSuccess(result)
    } catch (e: Exception) {
        onError("failed to adminDeleteStoreUser(): $e")
    }
}

suspend fun adminUpdateStoreUserStatus(
    storeId: Int,
    userUuid: String,
    status: STORE_USERS_STATUS,
    onSuccess: (ApiResponse<Boolean>) -> Unit,
    onError: (String) -> Unit,
) {
    try {
        val result: ApiResponse<Boolean> = safeApiCallStringBody(
            url = "${ROUTES.BACKEND.STORES.ADMIN_STORE_USERS}/$storeId/$userUuid/status",
            method = "PUT",
            body = Json.encodeToString(status)
        )
        onSuccess(result)
    } catch (e: Exception) {
        onError("failed to adminUpdateStoreUserStatus(): $e")
    }
}


suspend fun adminDeleteStoreUser(
    storeId: Int,
    userUuid: String,
    onSuccess: (ApiResponse<Boolean>) -> Unit,
    onError: (String) -> Unit,
) {
    try {
        val result: ApiResponse<Boolean> = safeApiCallStringBody(
            url = "${ROUTES.BACKEND.STORES.ADMIN_STORE_USERS}/$storeId/$userUuid",
            method = "DELETE",
        )
        onSuccess(result)
    } catch (e: Exception) {
        onError("failed to adminDeleteStoreUser(): $e")
    }
}

/*
    ======================================================
    Store User Invite Admin Functions
    ======================================================
*/

suspend fun adminFetchStoreUserInvites(
    pageNumber: Int,
    pageSize: Int,
    basicFilters: BasicTableFilters,
    onResponse: (ApiResponse<PaginatedResult<StoreUsersInviteDTO>>) -> Unit,
    onError: (String) -> Unit,
) {
    val urlWithParameters = ROUTES.BACKEND.STORES.ADMIN_STORE_USERS_INVITES +
            "?page=$pageNumber&pageSize=$pageSize" +
            "&dateColumn=${basicFilters.dateColumn}" +
            "&asc=${basicFilters.isAscending}" +
            "&before=${basicFilters.isBefore}" +
            "&date=${basicFilters.dateString}"

    try {
        val result: ApiResponse<PaginatedResult<StoreUsersInviteDTO>> = safeApiCallStringBody(
            url = urlWithParameters,
            method = "GET",
        )
        onResponse(result)
    } catch (e: Exception) {
        onError("failed to adminFetchStoreUserInvitesByStoreId(): $e")
    }
}

suspend fun adminFetchStoreUserInvitesByStoreId(
    storeId: Int,
    pageNumber: Int,
    pageSize: Int,
    basicFilters: BasicTableFilters,
    onResponse: (ApiResponse<PaginatedResult<StoreUsersInviteDTO>>) -> Unit,
    onError: (String) -> Unit,
) {
    val urlWithParameters = "${ROUTES.BACKEND.STORES.ADMIN_STORE_USERS_INVITES}/$storeId" +
            "?page=$pageNumber&pageSize=$pageSize" +
            "&dateColumn=${basicFilters.dateColumn}" +
            "&asc=${basicFilters.isAscending}" +
            "&before=${basicFilters.isBefore}" +
            "&date=${basicFilters.dateString}"

    try {
        val result: ApiResponse<PaginatedResult<StoreUsersInviteDTO>> = safeApiCallStringBody(
            url = urlWithParameters,
            method = "GET",
        )
        onResponse(result)
    } catch (e: Exception) {
        onError("failed to adminFetchStoreUserInvitesByStoreId(): $e")
    }
}

suspend fun adminCancelStoreUserInvite(
    storeId: Int,
    inviteUuid: String,
    onSuccess: (ApiResponse<Boolean>) -> Unit,
    onError: (String) -> Unit,
) {
    try {
        val result: ApiResponse<Boolean> = safeApiCallStringBody(
            url = "${ROUTES.BACKEND.STORES.ADMIN_STORE_USERS_INVITES}/$storeId/$inviteUuid",
            method = "DELETE",
        )
        onSuccess(result)
    } catch (e: Exception) {
        onError("failed to adminCancelStoreUserInvite(): $e")
    }
}

/*
    ======================================================
    Store Users Admin Functions
    ======================================================
 */

suspend fun adminFetchAllStoreUsers(
    pageNumber: Int,
    pageSize: Int,
    storeStatus: STORE_USERS_STATUS,
    basicFilters: BasicTableFilters,
    onResponse: (ApiResponse<PaginatedResult<StoreUsersDTO>>) -> Unit,
    onError: (String) -> Unit,
) {
    val urlWithParameters = ROUTES.BACKEND.STORES.ADMIN_STORE_USERS +
            "?page=$pageNumber&pageSize=$pageSize" +
            "&dateColumn=${basicFilters.dateColumn}" +
            "&asc=${basicFilters.isAscending}" +
            "&before=${basicFilters.isBefore}" +
            "&date=${basicFilters.dateString}" +
            "&status=${storeStatus.bit}"

    try {
        val result: ApiResponse<PaginatedResult<StoreUsersDTO>> = safeApiCallStringBody(
            url = urlWithParameters,
            method = "GET",
        )

        onResponse(result)
    } catch (e: Exception) {
        onError("failed to adminFetchAllStoreUsers(): $e")
        return
    }
}

suspend fun adminFetchStoreUsersByStoreId(
    storeId: Int,
    pageNumber: Int,
    pageSize: Int,
    storeStatus: STORE_USERS_STATUS,
    basicFilters: BasicTableFilters,
    onResponse: (ApiResponse<PaginatedResult<StoreUsersDTO>>) -> Unit,
    onError: (String) -> Unit,
) {
    val urlWithParameters = "${ROUTES.BACKEND.STORES.ADMIN_STORE_USERS}/$storeId" +
            "?page=$pageNumber&pageSize=$pageSize" +
            "&dateColumn=${basicFilters.dateColumn}" +
            "&asc=${basicFilters.isAscending}" +
            "&before=${basicFilters.isBefore}" +
            "&date=${basicFilters.dateString}" +
            "&status=${storeStatus.bit}"

    try {
        val result: ApiResponse<PaginatedResult<StoreUsersDTO>> = safeApiCallStringBody(
            url = urlWithParameters,
            method = "GET",
        )
        onResponse(result)
    } catch (e: Exception) {
        onError("failed to adminFetchStoreUsersByStoreId(): $e")
    }
}